import { Controller } from '@hotwired/stimulus'
import * as Turbo from '@hotwired/turbo'
import type BulkSelectController from '../bulk_select_controller'

interface TomSelectLike {
  isOpen?: boolean
  items?: string[]
  setValue: (value: string, silent: boolean) => void
  close?: () => void
  clear?: (silent: boolean) => void
}

export default class extends Controller {
  static targets = [
    'configurationSelect',
    'configurationFilterCheckbox',
    'mappingRow',
    'bulkConfigurationSelect',
    'responseTypeBadge',
    'clearConfigurationButton'
  ]

  static outlets = ['bulk-select']

  static values = {
    unsavedMessage: String
  }

  declare readonly configurationSelectTargets: HTMLSelectElement[]
  declare readonly configurationFilterCheckboxTargets: HTMLInputElement[]
  declare readonly mappingRowTargets: HTMLElement[]
  declare readonly bulkConfigurationSelectTarget: HTMLSelectElement
  declare readonly hasBulkConfigurationSelectTarget: boolean
  declare readonly bulkSelectOutlet: BulkSelectController
  declare readonly hasBulkSelectOutlet: boolean
  declare readonly responseTypeBadgeTargets: HTMLElement[]
  declare readonly clearConfigurationButtonTargets: HTMLButtonElement[]
  declare unsavedMessageValue: string
  declare readonly hasUnsavedMessageValue: boolean

  private dirty = false

  connect (): void {
    this.configurationSelectTargets.forEach(select => {
      this.syncResponseType(select, false)
    })
    this.syncSearchParams()
    if (this.isInternalControlMapping()) this.restoreInternalControlFilter()
    document.addEventListener('turbo:frame-render', this.onFrameRender)

    if (this.pageLeaveGuardsEnabled()) {
      document.addEventListener('turbo:before-visit', this.onBeforeVisit)
      window.addEventListener('beforeunload', this.onBeforeUnload)
    }
  }

  disconnect (): void {
    document.removeEventListener('turbo:frame-render', this.onFrameRender)
    document.removeEventListener('turbo:before-visit', this.onBeforeVisit)
    window.removeEventListener('beforeunload', this.onBeforeUnload)
  }

  syncSearchParams (): void {
    const form = this.frameworkQueryForm()
    if (form == null) return

    const url = new URL(window.location.href)
    const standardId = url.searchParams.get('standard_id') ??
      this.element.querySelector<HTMLSelectElement>('[data-cy="framework-selector"]')?.value ??
      null

    this.updateHiddenField(form, 'standard_id', standardId, true)
    this.updateHiddenField(form, 'domain_id', url.searchParams.get('domain_id'), true)
  }

  markDirty (): void {
    this.dirty = true
  }

  clearDirty (): void {
    this.dirty = false
  }

  updateResponseType (event: Event): void {
    const select = this.selectFromEvent(event)
    if (select == null) return

    this.syncResponseType(select, true)
  }

  clearConfiguration (event: Event): void {
    const select = this.configurationSelectInRow(event.currentTarget as Element)
    if (select == null) return

    this.resetSelect(select, '')
    select.dispatchEvent(new Event('change', { bubbles: true }))
  }

  resetBulkConfigurationSelect (): void {
    if (!this.hasBulkConfigurationSelectTarget) return

    this.resetSelect(this.bulkConfigurationSelectTarget, '')
  }

  applyBulkResponse (): void {
    if (!this.hasBulkConfigurationSelectTarget) return

    this.applyBulkSelectResponse()
  }

  clearBulkResponses (): void {
    this.applyBulkValue('', '')
  }

  clearConfigurationFilterSearch (): void {
    const filterSearchInput = this.element.querySelector<HTMLInputElement>('[data-cy=configuration-filter-search]')
    if (filterSearchInput == null) return

    filterSearchInput.value = ''
    this.element
      .querySelectorAll<HTMLElement>('[data-cy=configuration-filter] [data-search-options-target="option"]')
      .forEach(option => option.classList.remove('hidden'))
    filterSearchInput.dispatchEvent(new Event('input', { bubbles: true }))
  }

  persistInternalControlFilter (): void {
    if (!this.isInternalControlMapping()) return

    this.persistFilter()
  }

  clearInternalControlFilterPersistence (): void {
    if (!this.isInternalControlMapping()) return

    this.clearPersistedFilter()
  }

  selectAllVisibleMappings (event: Event): void {
    const selectAll = event.currentTarget as HTMLInputElement
    const checked = selectAll.checked

    this.mappingCheckboxes().forEach(checkbox => {
      checkbox.checked = checked
    })

    this.syncBulkSelection()
  }

  syncBulkSelection (): void {
    if (!this.hasBulkSelectOutlet) return

    const checkboxes = this.mappingCheckboxes()
    const checkedCount = checkboxes.filter(checkbox => checkbox.checked).length

    this.bulkSelectOutlet.countValue = checkedCount

    if (this.bulkSelectOutlet.hasSelectAllTarget) {
      this.bulkSelectOutlet.selectAllTarget.checked =
        checkboxes.length > 0 && checkedCount === checkboxes.length
    }
  }

  changeStandard (event: Event): void {
    const select = event.target as HTMLSelectElement
    const url = new URL(window.location.href)
    const savedValue = select.dataset.savedValue ?? select.value

    if (!this.confirmNavigation()) {
      this.resetSelect(select, savedValue)
      event.preventDefault()
      return
    }

    this.dirty = false
    url.searchParams.set('standard_id', select.value)
    url.searchParams.delete('domain_id')
    url.searchParams.delete('search')
    this.deleteControlResponseFilterParams(url)
    this.clearFrameworkControlSearchInput()
    this.setFilterCheckboxValues([])
    this.clearConfigurationFilterSearch()
    select.dataset.savedValue = select.value
    Turbo.visit(url.toString(), { frame: 'framework-control-response-mapping-content' })
    Turbo.navigator.history.replace(url)
    this.syncSearchParams()
  }

  confirmDomainNavigation (event: Event): void {
    if (this.confirmNavigation()) {
      this.dirty = false
      return
    }

    event.preventDefault()
  }

  confirmQueryNavigation (event: Event): void {
    if (this.confirmNavigation()) {
      this.dirty = false
      return
    }

    event.preventDefault()
    event.stopImmediatePropagation()

    if (this.isInternalControlMapping()) {
      this.restoreInternalControlQueryControls()
    } else {
      this.restoreFrameworkQueryControls()
    }
  }

  private readonly onBeforeVisit = (event: Event): void => {
    if (!this.dirty) return

    if (window.confirm(this.unsavedMessageValue)) {
      this.dirty = false
      return
    }

    event.preventDefault()
  }

  private readonly onBeforeUnload = (event: Event): void => {
    if (!this.dirty) return

    event.preventDefault()
    event.returnValue = true
  }

  private readonly onFrameRender = (event: Event): void => {
    const frameId = (event.target as HTMLElement).id
    if (!['framework-control-response-mapping-content', 'internal-control-mapping-content'].includes(frameId)) return

    this.configurationSelectTargets.forEach(select => {
      this.syncResponseType(select, false)
    })

    if (frameId === 'framework-control-response-mapping-content') {
      this.syncSearchParams()
      return
    }

    this.restoreInternalControlFilter()
  }

  private filterStorageKey (): string {
    return 'internal-control-response-mapping-filter'
  }

  private persistFilter (): void {
    sessionStorage.setItem(this.filterStorageKey(), JSON.stringify(this.selectedFilterValues()))
  }

  private restoreInternalControlFilter (): void {
    const urlValues = this.controlResponseFilterValuesFromUrl()
    const stored = sessionStorage.getItem(this.filterStorageKey())

    if (urlValues.length > 0) {
      this.setFilterCheckboxValues(urlValues)
      this.persistFilter()
      return
    }

    if (stored == null) return

    const storedValues = JSON.parse(stored) as string[]
    this.setFilterCheckboxValues(storedValues)
    if (storedValues.length === 0) return

    window.requestAnimationFrame(() => {
      this.internalControlQueryForm()?.requestSubmit()
    })
  }

  private clearPersistedFilter (): void {
    sessionStorage.removeItem(this.filterStorageKey())
  }

  private clearFrameworkControlSearchInput (): void {
    const searchInput = this.frameworkControlSearchInput()
    if (searchInput != null) searchInput.value = ''
  }

  private restoreFrameworkQueryControls (): void {
    const url = new URL(window.location.href)
    const searchInput = this.frameworkControlSearchInput()
    const clearSearchButton = this.element.querySelector<HTMLElement>('[data-cy="framework-control-search-clear"]')

    if (searchInput != null) {
      searchInput.value = url.searchParams.get('search') ?? ''
      clearSearchButton?.classList.toggle('hidden', searchInput.value === '')
    }

    this.setFilterCheckboxValues(this.controlResponseFilterValuesFromUrl())
  }

  private restoreInternalControlQueryControls (): void {
    const url = new URL(window.location.href)
    const searchInput = this.element.querySelector<HTMLInputElement>('[data-cy="internal-control-search-input"]')
    const clearSearchButton = this.element.querySelector<HTMLElement>('[data-cy="internal-control-search-clear"]')

    if (searchInput != null) {
      searchInput.value = url.searchParams.get('search') ?? ''
      clearSearchButton?.classList.toggle('hidden', searchInput.value === '')
    }

    this.setFilterCheckboxValues(this.controlResponseFilterValuesFromUrl())
    this.persistFilter()
  }

  private setFilterCheckboxValues (values: string[]): void {
    this.configurationFilterCheckboxTargets.forEach(checkbox => {
      checkbox.checked = values.includes(checkbox.value)
    })
    document.dispatchEvent(new CustomEvent('count-checkboxes:update'))
  }

  private selectedFilterValues (): string[] {
    return this.configurationFilterCheckboxTargets
      .filter(checkbox => checkbox.checked)
      .map(checkbox => checkbox.value)
  }

  private controlResponseFilterValuesFromUrl (): string[] {
    const params = new URL(window.location.href).searchParams

    return [...params.getAll('control_response_filter[]'), ...params.getAll('control_response_filter')]
      .filter(value => value !== '')
  }

  private deleteControlResponseFilterParams (url: URL): void {
    url.searchParams.delete('control_response_filter[]')
    url.searchParams.delete('control_response_filter')
  }

  private frameworkQueryForm (): HTMLFormElement | null {
    return this.element.querySelector<HTMLFormElement>('#framework-control-mapping-query-form')
  }

  private internalControlQueryForm (): HTMLFormElement | null {
    return this.element.querySelector<HTMLFormElement>('#internal-control-mapping-query-form')
  }

  private isInternalControlMapping (): boolean {
    return this.element.id === 'internal-control-mapping-bulk-select'
  }

  private frameworkControlSearchInput (): HTMLInputElement | null {
    return this.element.querySelector<HTMLInputElement>('[data-cy="framework-control-search-input"]')
  }

  private updateHiddenField (
    form: HTMLFormElement,
    name: string,
    value: string | null,
    ignoreReset = false
  ): void {
    const existing = form.querySelector<HTMLInputElement>(`input[type="hidden"][name="${name}"]`)

    if (value == null || value === '') {
      existing?.remove()
      return
    }

    const field = existing ?? document.createElement('input')
    field.type = 'hidden'
    field.name = name
    field.value = value
    if (ignoreReset) field.dataset.ignoreReset = 'true'
    if (existing == null) form.appendChild(field)
  }

  private applyBulkSelectResponse (): void {
    const select = this.bulkConfigurationSelectTarget
    const configurationId = select.value
    const selectedOption = select.selectedOptions[0]
    const responseTypeLabel = configurationId === ''
      ? ''
      : (selectedOption?.dataset.responseTypeLabel ?? '')

    this.applyBulkValue(configurationId, responseTypeLabel)
    this.finishBulkSelectApply()
  }

  private applyBulkValue (configurationId: string, responseTypeLabel: string): void {
    this.selectedMappingRows().forEach(row => {
      const select = row.querySelector<HTMLSelectElement>('[data-framework-control-response-mapping-target~="configurationSelect"]')
      if (select == null) return

      this.resetSelect(select, configurationId)
      this.syncResponseType(select, false, responseTypeLabel)
    })

    this.markDirty()
    if (this.hasBulkSelectOutlet) this.bulkSelectOutlet.deselectAll()
  }

  private syncResponseType (
    select: HTMLSelectElement,
    markDirty: boolean,
    responseTypeLabel: string | null = null
  ): void {
    const row = select.closest('tr')
    if (row == null) return

    const badge = this.responseTypeBadgeTargets.find(target => row.contains(target))
    const badgeLabel = badge?.querySelector('span')
    const selectedOption = select.selectedOptions[0]
    const label = responseTypeLabel ?? (select.value === '' ? '' : (selectedOption?.dataset.responseTypeLabel ?? ''))

    if (badgeLabel != null) {
      badgeLabel.textContent = label
    }

    badge?.classList.toggle('hidden', label === '')

    const clearButton = this.clearConfigurationButtonTargets.find(target => row.contains(target))
    if (clearButton != null) {
      clearButton.disabled = select.value === ''
    }

    row.dataset.configurationId = select.value
    if (markDirty) this.markDirty()
  }

  private selectFromEvent (event: Event): HTMLSelectElement | null {
    const target = event.target as HTMLElement | null
    if (target == null) return null

    if (target instanceof HTMLSelectElement) return target

    return this.configurationSelectInRow(target)
  }

  private configurationSelectInRow (element: Element): HTMLSelectElement | null {
    return element.closest('tr')?.querySelector('select') ?? null
  }

  private mappingCheckboxes (): HTMLInputElement[] {
    return this.mappingRowTargets.flatMap(row => {
      const checkbox = row.querySelector<HTMLInputElement>('[data-cy="select-control-mapping"], [data-cy="select-internal-control-mapping"]')

      return checkbox == null ? [] : [checkbox]
    })
  }

  private selectedMappingRows (): HTMLTableRowElement[] {
    return Array.from(this.element.querySelectorAll<HTMLInputElement>('[data-bulk-select-target~="checkbox"]:checked'))
      .map(checkbox => checkbox.closest('tr'))
      .filter((row): row is HTMLTableRowElement => row != null)
  }

  private confirmNavigation (): boolean {
    return !this.dirty || window.confirm(this.unsavedMessageValue)
  }

  private pageLeaveGuardsEnabled (): boolean {
    return this.hasUnsavedMessageValue &&
      this.unsavedMessageValue.length > 0
  }

  private tomSelectFor (select: HTMLSelectElement): TomSelectLike | null {
    return (select as HTMLSelectElement & { tomselect?: TomSelectLike }).tomselect ?? null
  }

  private resetSelect (select: HTMLSelectElement, value: string): void {
    const tomSelect = this.tomSelectFor(select)

    if (tomSelect != null) {
      if (tomSelect.isOpen === true && tomSelect.close != null) tomSelect.close()

      if (value === '' && tomSelect.clear != null) {
        tomSelect.clear(true)
      } else {
        tomSelect.setValue(value, true)
      }

      return
    }

    select.value = value
  }

  private finishBulkSelectApply (): void {
    this.resetBulkConfigurationSelect()

    this.element.querySelectorAll<HTMLDialogElement>('dialog.slideover[open]').forEach(dialog => {
      dialog.removeAttribute('closing')
      dialog.close()
    })
  }
}
